import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ownerPhotoSrc } from "@/lib/regions";
import { Loader2, Upload, UserRound } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024;

export const OwnerPhotoUpload = ({ circleId, value, onUploaded }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Please choose a JPG, PNG or WebP image.");
    if (file.size > MAX_BYTES) return toast.error("Image must be 5 MB or smaller.");
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/circles/${circleId}/owner-photo`, body, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.owner_photo_url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="owner-photo-upload">
      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center ring-2 ring-gold/30">
        {value ? <img src={ownerPhotoSrc(value)} alt="Owner" className="w-full h-full object-cover" data-testid="owner-photo-preview" /> : <UserRound size={22} className="text-gray-400" />}
      </div>
      {circleId ? (
        <>
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="owner-photo-input" />
          <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid="owner-photo-upload-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-navy text-navy text-sm font-semibold hover:bg-navy hover:text-white transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {value ? "Replace photo" : "Upload photo"}
          </button>
          <span className="text-xs text-gray-500">JPG, PNG or WebP, up to 5 MB. Goes live as soon as it uploads.</span>
        </>
      ) : (
        <span className="text-xs text-gray-500">Save the region first, then upload a photo.</span>
      )}
    </div>
  );
};
